import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGetAmbassadorsQuery, useGetAmbassadorProfileQuery, useLazyGetAmbassadorCollectionQuery } from '../../redux/apis/endpoints/ambassadorApi';
import './AmbassadorLandingPage.css';
import VTOLoginConfirmationPortal from '../virtual-try-on/VTOLoginConfirmationPortal';
import ProfileShareModal from '../profile-share/ProfileShareModal';
import HowItWorksFlow from '../how-it-works/HowItWorksFlow';


const AmbassadorLandingPage = () => {
  const navigate = useNavigate();
  const [signInOpen, setSignInOpen] = useState(false);
  const [loadCollection] = useLazyGetAmbassadorCollectionQuery();
  const [selected, setSelected] = useState(null);
  const { currentData: share, isFetching: loadingProfile, isError: profileError } = useGetAmbassadorProfileQuery(selected?.personaId, { skip: !selected });
  const location = useLocation();
  const { data: ambassadors, isLoading, isError } = useGetAmbassadorsQuery();
  const fieldRef = useRef(null);
  const [fieldWidth, setFieldWidth] = useState(1100);
  useEffect(() => {
    const observer = new ResizeObserver(([entry]) => setFieldWidth(entry.contentRect.width));
    if (fieldRef.current) observer.observe(fieldRef.current);
    return () => observer.disconnect();
  }, []);
  const variation = useRef(Array.from({ length: 30 }, () => Math.random()));
  const fieldHeight = fieldWidth < 760 ? 850 : Math.min(960, Math.max(700, fieldWidth * 0.66));
  const positions = useMemo(() => {
    const compact = fieldWidth < 760;
    const slots = compact
      ? [[16,8],[50,10],[84,7],[30,23],[70,22],[12,35],[88,34],[12,65],[88,66],[30,78],[70,77],[16,92],[50,90],[84,93]]
      : [[9,17],[29,13],[50,15],[71,12],[91,17],[9,40],[91,40],[9,64],[91,64],[9,85],[29,88],[50,85],[71,88],[91,85]];
    return (ambassadors || []).map((_, i) => {
      const [x, y] = slots[i % slots.length];
      const random = variation.current[i % 30];
      const size = compact ? Math.min(146, fieldWidth * (0.245 + random * 0.02)) : Math.min(224, fieldWidth * (0.125 + random * 0.018));
      return { x: fieldWidth * x / 100, y: fieldHeight * y / 100, size };
    });
  }, [ambassadors, fieldWidth, fieldHeight]);

  // If someone arrives at / with ?share=<token> or ?token=<sso>, redirect to
  // /my-closet so the existing share/SSO flow handles it.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.has('share') || params.has('token')) {
      navigate(`/my-closet${location.search}`, { replace: true });
    }
  }, [location.search, navigate]);

  const handleCircleClick = (persona) => {
    if (!persona.shareUrl) return;
    const token = new URL(persona.shareUrl, window.location.origin).searchParams.get('share');
    if (token) setSelected({ personaId: persona.personaId, name: persona.name, avatarUrl: persona.avatarUrl });
  };

  return (
    <div className="ambassador-landing">
      <header className="landing-header">
        <a className="landing-logo" href="/">SUSU Closet</a>
        <nav className="landing-nav" aria-label="Main navigation">
          <a href="#about">About</a><a href="/contact">Contact</a>
          <a href="/my-closet">My closet</a>
          {!localStorage.getItem('token') && <button onClick={() => setSignInOpen(true)}>Sign in</button>}
        </nav>

      </header>

      <main className="landing-main">

        <div className="ambassador-grid-container" ref={fieldRef} style={{ height: fieldHeight }}>
        <div className="hero-text">
          <span className="landing-eyebrow">MANY WORLDS. ONE CLOSET.</span>
          <h1>A little outfit inspiration.<br /><em>A whole new you.</em></h1>
          <p>Pick a person. Peek into their closet. Make a look your own.</p>
        <div className="cta-section">
          <button className="cta-btn btn-web" onClick={() => navigate('/my-closet')}>
            Enter closet
          </button>
        </div>
        </div>

          {isLoading && <div className="loading-indicator">Loading ambassadors...</div>}
          {isError && <div className="error-message">Failed to load ambassadors. Please try again later.</div>}

          {!isLoading && !isError && ambassadors && (
            <div className="ambassador-grid" style={{ height: fieldHeight }}>
              {ambassadors.map((ambassador, index) => (
                <button
                  type="button"
                  key={ambassador.personaId}
                  className="ambassador-circle-wrapper"
                  style={{ '--delay': `${index * -0.7}s`, left: positions[index]?.x, top: positions[index]?.y, '--bubble-size': `${positions[index]?.size}px` }}
                  onClick={() => handleCircleClick(ambassador)}
                  disabled={!ambassador.shareUrl}
                  aria-label={`Open ${ambassador.name}'s shared closet`}
                >
                  <span className="ambassador-circle">
                    <img src={ambassador.featuredLookUrl || ambassador.avatarUrl}
                      alt={`${ambassador.name}'s look`} loading={index < 6 ? 'eager' : 'lazy'}
                      onError={(event) => {
                        if (!event.currentTarget.dataset.fallback) {
                          event.currentTarget.dataset.fallback = 'true';
                          event.currentTarget.src = ambassador.avatarUrl;
                        } else event.currentTarget.style.visibility = 'hidden';
                      }} />
                    <span className="circle-invitation">{ambassador.name}<small>Explore closet ↗</small></span>
                  </span>

                </button>
              ))}
            </div>
          )}
        </div>


        {selected && loadingProfile && <div className="profile-loading" role="status">Opening {selected.name}'s closet…</div>}
        {selected && profileError && <div className="profile-loading" role="alert">Couldn’t open this closet. <button onClick={() => setSelected(null)}>Close</button></div>}
        <ProfileShareModal isOpen={!!selected && !loadingProfile && !!share?.profile}
          profile={share?.profile ? { ...share.profile, displayName: share.profile.displayName || selected?.name, avatarUrl: share.profile.avatarUrl || selected?.avatarUrl } : null}
          loadFolder={async (folder) => {
            const token = new URL(folder.shareUrl, window.location.origin).searchParams.get('share');
            return loadCollection({ personaId: selected.personaId, token }).unwrap();
          }}
          onAdd={(url) => window.location.assign(new URL(url, selected ? new URL(ambassadors.find(p => p.personaId === selected.personaId).shareUrl).origin : window.location.origin).href)}
          onClose={() => setSelected(null)} />
      </main>
      <section className="landing-about" id="about" aria-labelledby="about-title">
        <span className="landing-eyebrow">YOUR STYLE HAS VALUE.</span>
        <h2 id="about-title">Inspire a look. <em>Earn from your style.</em></h2>
        <p>Build your closet, put together your favorite fits, and share them with the world. When someone completes an eligible try-on of a piece you shared &mdash; or one you designed &mdash; you earn.</p>
        <div className="landing-steps">
          <div><span>01</span><h3>Fill your closet</h3><p>Upload your own pieces, or wear something from Explore &mdash; designer work and looks other members have shared.</p></div>
          <div><span>02</span><h3>Share what you make</h3><p>Share a look, a lookbook, or a whole closet with one link. Designers list a piece once and it is live.</p></div>
          <div><span>03</span><h3>Get rewarded</h3><p>Track eligible try-on earnings in My Closet. Connect your payout account and enable monthly payouts when you meet the minimum balance.</p></div>
        </div>
        <button className="cta-btn btn-web" onClick={() => navigate('/my-closet')}>Create your closet</button>
      </section>
      <HowItWorksFlow />
      <section className="landing-contact" id="contact"><h2>Let’s talk style.</h2><p>Questions, ideas, or a collaboration in mind?</p><a href="/contact">Send us a message ↗</a></section>
      <footer className="landing-footer">
        <a className="landing-logo" href="/">SUSU Closet</a>
        <nav aria-label="Footer navigation"><a href="#about">About</a><a href="/contact">Contact</a><a href="/privacy">Privacy Policy</a><a href="https://sheyou.co/terms-of-service">Terms of Service</a></nav>
        <small>© {new Date().getFullYear()} SUSU Closet</small>
      </footer>
      {signInOpen && <VTOLoginConfirmationPortal isOpen onClose={() => setSignInOpen(false)} onCancel={() => setSignInOpen(false)} onLogin={() => { setSignInOpen(false); navigate('/my-closet'); }} />}
    </div>
  );
};

export default AmbassadorLandingPage;
